import React, { memo, useCallback, useEffect } from 'react';
import { ScrollView, StyleSheet } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';

import { PlayerProjectionFragment } from '@/api/events/query.generated';
import FireIcon from '@/assets/icons/fire';
import { ChipButton } from '@/components/ChipButton';
import { SizedBox as SizeBoxComponent } from '@/components/SizedBox';
import {
    usePlayerFiltersActions,
    usePlayerFiltersScrollControls,
    usePlayerFiltersState,
} from '@/feature/lobby/hooks/use-player-filters';
import { common } from '@/styles/styles';
import { showScrollIndicator } from '@/utils/constants-platform-specific';

import { POPULAR_PLAYERS_FILTER } from '../utils/players';

const SizedBox = () => <SizeBoxComponent value={8} />;

export const PlayerProjectionsFilters = memo(({ filtersData }: { filtersData: PlayerProjectionFragment[] }) => {
    const { playerProjectionFilter } = usePlayerFiltersState();
    const { setPlayerProjectionFilter } = usePlayerFiltersActions();
    const { scrollToFilters } = usePlayerFiltersScrollControls();

    const onSelect = useCallback(
        (key: string, label: string) => {
            scrollToFilters();
            setPlayerProjectionFilter(key, label);
        },
        [scrollToFilters, setPlayerProjectionFilter]
    );

    const renderItem = useCallback(
        ({ item }: { item: PlayerProjectionFragment }) => (
            <ProjectionFilterChip
                itemKey={item.key}
                label={item.label ?? ''}
                active={playerProjectionFilter === item.key}
                isPopular={item.key === POPULAR_PLAYERS_FILTER}
                onSelect={onSelect}
            />
        ),
        [playerProjectionFilter, onSelect]
    );

    useEffect(() => {
        const setupInitialFilter = () => {
            if (!filtersData || filtersData.length === 0) {
                //data is not fetched yet, skip
                return;
            }
            if (filtersData.some(projection => projection.key === playerProjectionFilter)) {
                //if the default filter (Popular) exists in the available filters, we keep it
                return;
            }
            //otherwise, we set the first filter as the default one
            setPlayerProjectionFilter(filtersData[0].key, filtersData[0].label);
        };
        setupInitialFilter();
    }, [filtersData, playerProjectionFilter, setPlayerProjectionFilter]);

    if (filtersData.length === 0) {
        // there's a bug in section list where it doesn't render the next page when scrolling
        // not rendering the list when there's no data somehow fixes it: https://github.com/facebook/react-native/issues/39421
        return null;
    }

    /*
    Nesting the FlatList inside a ScrollView to allow horizontal scrolling - the "spring" effect, even when the list of item is short and doesn't require scrolling
    If they have different scroll directions, there will be no warning.
    Also it fixed the swipe gesture in the Chip bar that allows the user to navigate between tabs
    */
    return (
        <ScrollView style={common.flex} scrollEnabled={false}>
            <FlatList
                horizontal
                data={filtersData}
                ListFooterComponent={<SizeBoxComponent value={16} />}
                ListHeaderComponent={<SizeBoxComponent value={16} />}
                ItemSeparatorComponent={SizedBox}
                showsHorizontalScrollIndicator={showScrollIndicator}
                contentContainerStyle={styles.flatListStyle}
                renderItem={renderItem}
            />
        </ScrollView>
    );
});

PlayerProjectionsFilters.displayName = 'PlayerProjectionsFilters';

const ProjectionFilterChip = memo(
    ({
        itemKey,
        label,
        active,
        isPopular,
        onSelect,
    }: {
        itemKey: string;
        label: string;
        active: boolean;
        isPopular: boolean;
        onSelect: (key: string, label: string) => void;
    }) => {
        const handlePress = useCallback(() => {
            onSelect(itemKey, label);
        }, [onSelect, itemKey, label]);

        return (
            <ChipButton
                label={label}
                active={active}
                onPress={handlePress}
                testID={`projectionsChipButton-${itemKey}`}
                iconLeft={isPopular ? <FireIcon /> : undefined}
            />
        );
    }
);

const styles = StyleSheet.create({
    flatListStyle: {
        paddingVertical: 16,
        minWidth: '100%',
    },
});
